import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Autocomplete,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  useLunchMenu, 
  useEmployeeNames, 
  useMyOrder, 
  useCreateAdminOrder, 
  useDeleteAdminOrder 
} from '../../hooks/useLunch';
import { ShoppingCart as ShoppingCartIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { LUNCH } from '../../constants';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
}

const EmployeeOrder: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(LUNCH.DEFAULT_PRICE);
  const [note, setNote] = useState<string>('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const dateString = selectedDate.toISOString().split('T')[0];
  const { data: menu, isLoading: menuLoading } = useLunchMenu(dateString);
  const { data: employees = [], isLoading: loadingEmployees } = useEmployeeNames();
  const { data: employeeOrder, isLoading: loadingOrder } = useMyOrder(
    selectedEmployee?.id, 
    dateString
  );
  const createOrder = useCreateAdminOrder();
  const deleteOrder = useDeleteAdminOrder();

  // Update selected items and total price when order data changes
  useEffect(() => {
    if (employeeOrder?.items) {
      setSelectedItems(employeeOrder.items.map((item: any) => item.itemId));
      setTotalPrice(employeeOrder.totalPrice);
      setNote(employeeOrder.note || '');
    } else {
      setSelectedItems([]);
      setTotalPrice(LUNCH.DEFAULT_PRICE);
      setNote('');
    }
  }, [employeeOrder]);

  const handleItemToggle = (itemId: string) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedEmployee?.id) {
      setError('Please select an employee');
      setSuccess('');
      return;
    }

    if (selectedItems.length === 0) {
      setError('Please select at least one item');
      setSuccess('');
      return;
    }

    try {
      await createOrder.mutateAsync({
        employeeId: selectedEmployee.id,
        date: dateString,
        itemIds: selectedItems,
        totalPrice: totalPrice,
        note: note
      });

      setSuccess('Order saved successfully');
      setError('');
    } catch (err) {
      setError('Failed to save order');
      setSuccess('');
    }
  };

  const handleDelete = async () => {
    if (!selectedEmployee?.id) return;

    try {
      await deleteOrder.mutateAsync({
        employeeId: selectedEmployee.id,
        date: dateString
      });

      setSuccess('Order cancelled successfully');
      setError('');
    } catch (err) {
      setError('Failed to cancel order');
      setSuccess('');
    }
  };

  const isLoading = menuLoading || loadingOrder;

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 350px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Select Date
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a date to place an order
            </Typography>
            <LocalizationProvider 
              dateAdapter={AdapterDateFns}
              adapterLocale={{...enUS, options: { ...enUS.options, weekStartsOn: 1 }}}
            >
              <DateCalendar
                value={selectedDate}
                onChange={(newDate) => newDate && setSelectedDate(newDate)}
              />
            </LocalizationProvider>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ flex: '2 1 600px', minWidth: 300 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order for {format(selectedDate, 'dd.MM.yyyy.')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an employee and place lunch order for them
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              value={selectedEmployee}
              onChange={(_event, newValue) => setSelectedEmployee(newValue)}
              disabled={loadingEmployees}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Employee"
                  placeholder="Type to search..."
                />
              )}
              sx={{ mb: 3 }}
            />

            {!selectedEmployee ? (
              <Alert severity="info">
                Please select an employee to view the menu and place an order for them.
              </Alert>
            ) : isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !menu ? (
              <Alert severity="info">
                No menu available for this date yet.
              </Alert>
            ) : menu.items.length === 0 ? (
              <Alert severity="info">
                The menu for this date is empty.
              </Alert>
            ) : (
              <>
                <Paper sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                  {menu.items.map((item: any) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onChange={() => handleItemToggle(item.id)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 1 }}>
                            <Typography variant="body2">{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                              {item.price} RSD
                            </Typography>
                          </Box>
                        }
                        sx={{ flex: 1, m: 0 }}
                      />
                    </Box>
                  ))}
                </Paper>

                {employeeOrder && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This employee has an existing order for this date with {employeeOrder.items.length} item(s).
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={createOrder.isPending || selectedItems.length === 0}
                    startIcon={createOrder.isPending ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                  >
                    {employeeOrder ? 'Update Order' : 'Place Order'}
                  </Button>

                  {employeeOrder && (
                    <IconButton
                      color="error"
                      onClick={handleDelete}
                      disabled={deleteOrder.isPending}
                      sx={{ border: 1, borderColor: 'error.main' }}
                    >
                      {deleteOrder.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
                    </IconButton>
                  )}
                </Box>

                {selectedItems.length > 0 && (
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total for this day:
                      </Typography>
                      <TextField
                        type="number"
                        value={totalPrice}
                        onChange={(e) => setTotalPrice(Number(e.target.value))}
                        InputProps={{
                          endAdornment: <Typography sx={{ ml: 1 }}>RSD</Typography>,
                        }}
                        sx={{ 
                          width: 150,
                          '& .MuiInputBase-root': {
                            fontWeight: 'bold',
                            fontSize: '1.25rem'
                          }
                        }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      label="Note"
                      multiline
                      rows={2}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Add any special instructions or notes..."
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {selectedItems.length} item(s) selected
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EmployeeOrder;
